import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable16 = ({ data }) => {
  const pdfRef = useRef();

  // Clean Data + Auto Compute Growth
  const processed = data.map((item) => {
    const start = Number(item.year_2018);
    const end = Number(item.year_2024);
    const pct = (((end - start) / start) * 100).toFixed(2);

    return {
      ...item,
      pct,
      values: [
        Number(item.year_2018),
        Number(item.year_2019),
        Number(item.year_2020),
        Number(item.year_2021),
        Number(item.year_2022),
        Number(item.year_2023),
        Number(item.year_2024),
      ],
    };
  });

  // KPI Insights
  const totalGrowth = processed.reduce(
    (sum, row) => sum + (Number(row.year_2024) - Number(row.year_2018)),
    0
  );

  const avgGrowth = (totalGrowth / processed.length).toFixed(2);

  const highestGrowthItem = processed.reduce((prev, curr) =>
    curr.pct > prev.pct ? curr : prev
  );

  // Chart Configuration
  const yearLabels = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
  const chartData = {
    labels: processed.map((item) => item.education_label),
    datasets: yearLabels.map((year, index) => ({
      label: `Year ${year}`,
      data: processed.map((row) => row.values[index]),
      backgroundColor: `#B7950B`,
      borderColor: `#B7950B`,
      borderWidth: 1,
    })),
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Growth: Less than High School (2018–2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment" } },
      x: { title: { display: true, text: "Education Level" } },
    },
  };

  // PDF Function
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 10, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 50, 20);
      pdf.line(10, 32, 200, 32);

      // Main Content
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 5, 35, width, height);

      // Footer
      pdf.line(10, 260, 200, 260);
      pdf.addImage(bodex, "PNG", 15, 265, 35, 13);
      pdf.addImage(kpi, "PNG", 55, 265, 18, 18);

      pdf.setFontSize(10);
      pdf.text(`Version: 20250916.v3.198`, 150, 276);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 145, 292);
      pdf.text(`Source: ${window.location.href}`, 10, 292);

      pdf.save("EngTable16.pdf");
    });
  };

  return (
    <>
      {/* Page Content */}
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-4">
          Employment Growth for "Less than High School" (2018–2024)
        </h2>

        {/* KPI Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-indigo-100 rounded-lg shadow">
            <p className="font-semibold text-indigo-900">📈 Average Growth</p>
            <p className="text-xl font-bold">{avgGrowth} Jobs</p>
          </div>

          <div className="p-4 bg-green-100 rounded-lg shadow">
            <p className="font-semibold text-green-900">
              🚀 Highest Growth Category
            </p>
            <p className="text-sm text-gray-800">
              {highestGrowthItem.education_label}
            </p>
            <p className="font-bold text-green-800">{highestGrowthItem.pct}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-lg shadow">
            <p className="font-semibold text-yellow-900">📊 Total Growth</p>
            <p className="text-xl font-bold">
              {totalGrowth.toLocaleString("en-US")} Jobs
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-3">Education Level</th>
                    {yearLabels.map((y) => (
                      <th key={y}>Year {y}</th>
                    ))}
                    <th>Growth %</th>
                  </tr>
                </thead>
                <tbody>
                  {processed.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="font-semibold p-2">
                        {row.education_label}
                      </td>
                      {row.values.map((v, i) => (
                        <td key={i}>{v.toLocaleString("en-US")}</td>
                      ))}
                      <td className="text-green-700 font-semibold">
                        {row.pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable16;
